import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/format";
import { PageHeader } from "@/components/Page";

export default function Einstellungen() {
  const [c, setC] = useState(null);
  const [tpl, setTpl] = useState({ name: "", text: "" });
  const [client, setClient] = useState({ name: "", phone: "", email: "", address: "" });

  const load = async () => setC((await api.get("/company")).data);
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      const r = await api.put("/company/settings", patch);
      setC(r.data);
      toast.success("Einstellungen gespeichert");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!c) return null;

  const addTpl = () => {
    if (!tpl.name || !tpl.text) return toast.error("Name und Text erforderlich");
    const list = [...(c.confirmation_templates || []), { id: `tpl_${Date.now()}`, ...tpl }];
    save({ confirmation_templates: list });
    setTpl({ name: "", text: "" });
  };
  const delTpl = (id) => save({ confirmation_templates: (c.confirmation_templates || []).filter((t) => t.id !== id) });

  const addClient = () => {
    if (!client.name) return toast.error("Name erforderlich");
    const list = [...(c.clients || []), { id: `cl_${Date.now()}`, ...client }];
    save({ clients: list });
    setClient({ name: "", phone: "", email: "", address: "" });
  };
  const delClient = (id) => save({ clients: (c.clients || []).filter((x) => x.id !== id) });

  return (
    <div>
      <PageHeader title="Firmen-Einstellungen" subtitle="Adresse, E-Mail-Modus, Vorlagen und Kunden." testid="einstellungen-header" />
      <div className="grid gap-6 max-w-3xl">
        <Card className="p-6 border-slate-200 rounded-xl space-y-4">
          <h3 className="font-semibold text-slate-900">Allgemein</h3>
          <div className="space-y-2">
            <Label>Firmenadresse</Label>
            <Input data-testid="settings-address" defaultValue={c.company_address || ""} onBlur={(e) => e.target.value !== (c.company_address || "") && save({ company_address: e.target.value })} placeholder="Straße, PLZ Ort" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>E-Mail-Modus</Label>
              <Select value={c.email_mode || "direct"} onValueChange={(v) => save({ email_mode: v })}>
                <SelectTrigger data-testid="settings-email-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Direkt an Kunden</SelectItem>
                  <SelectItem value="self">An eigene Firma</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Antwort-E-Mail (reply-to)</Label>
              <Input data-testid="settings-reply-to" defaultValue={c.email_reply_to || ""} onBlur={(e) => e.target.value !== (c.email_reply_to || "") && save({ email_reply_to: e.target.value })} placeholder="info@firma.de" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Benachrichtigung vor Foto-Löschung</Label>
            <Input data-testid="settings-photo-notify" defaultValue={c.photo_deletion_notify_email || ""} onBlur={(e) => e.target.value !== (c.photo_deletion_notify_email || "") && save({ photo_deletion_notify_email: e.target.value })} placeholder="benachrichtigung@firma.de (leer = keine Erinnerung)" />
            <p className="text-xs text-slate-400">Erhält ~1 Tag vor der automatischen Löschung von Auftrags-Fotos eine Erinnerung. Leer = keine Erinnerung.</p>
          </div>
          <div className="space-y-2">
            <Label>Willkommens-Text für Zugangs-E-Mails</Label>
            <Textarea data-testid="settings-welcome-message" defaultValue={c.welcome_message || ""} onBlur={(e) => e.target.value !== (c.welcome_message || "") && save({ welcome_message: e.target.value })} placeholder="Herzlich willkommen im Team! Wir freuen uns auf die Zusammenarbeit." />
            <p className="text-xs text-slate-400">Erscheint als Begrüßung in der E-Mail mit den Zugangsdaten. Beim Versand pro Mitarbeiter überschreibbar.</p>
          </div>
          <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
            <span className="text-sm font-medium text-slate-700">Super-Admin darf Mitarbeiter anlegen<span className="block text-xs font-normal text-slate-400">Erlaubt dem Betreiber (teamtempus), Mitarbeiter für diese Firma anzulegen.</span></span>
            <Switch checked={!!c.super_admin_user_creation_enabled} onCheckedChange={(v) => save({ super_admin_user_creation_enabled: v })} data-testid="settings-super-user-creation" />
          </label>
          <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
            <span className="text-sm font-medium text-slate-700">Betreiber darf meine Firma verwalten<span className="block text-xs font-normal text-slate-400">Erlaubt dem Betreiber, Ihre Firma im Onboarding-Modus vollständig zu verwalten (Mitarbeiter, Aufträge, Branding, Einstellungen). Ausschalten sperrt den Zugriff.</span></span>
            <Switch checked={c.super_admin_manage_enabled !== false} onCheckedChange={(v) => save({ super_admin_manage_enabled: v })} data-testid="settings-super-manage" />
          </label>
          <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
            <span className="text-sm font-medium text-slate-700">Resturlaub ins Folgejahr übertragen<span className="block text-xs font-normal text-slate-400">Nicht genommene Urlaubstage werden dem nächsten Jahr gutgeschrieben.</span></span>
            <Switch checked={!!c.vacation_carryover_enabled} onCheckedChange={(v) => save({ vacation_carryover_enabled: v })} data-testid="settings-vacation-carryover" />
          </label>
          <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
            <span className="text-sm font-medium text-slate-700">Schichten bei Nichtverfügbarkeit blockieren<span className="block text-xs font-normal text-slate-400">Ist der Mitarbeiter am Tag als „nicht verfügbar" eingetragen, wird die Schicht abgelehnt (statt nur gewarnt).</span></span>
            <Switch checked={!!c.shift_block_unavailable} onCheckedChange={(v) => save({ shift_block_unavailable: v })} data-testid="settings-shift-block" />
          </label>
          {(c.features || []).includes("margin") && (
            <div className="space-y-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Label>Zielmarge / Warnschwelle (%)</Label>
              <Input type="number" step="0.5" className="max-w-[160px]" data-testid="settings-margin-threshold" defaultValue={c.margin_threshold_pct ?? ""} onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (c.margin_threshold_pct ?? null)) save({ margin_threshold_pct: v }); }} placeholder="z. B. 15" />
              <p className="text-xs text-slate-400">Projekte unter dieser Marge werden im Margin-Report rot markiert, knapp darüber (bis +5 %) gelb. Leer = keine Ampel.</p>
            </div>
          )}
        </Card>

        <Card className="p-6 border-slate-200 rounded-xl space-y-4">
          <h3 className="font-semibold text-slate-900">Standardtext-Vorlagen</h3>
          <div className="space-y-2">
            {(c.confirmation_templates || []).length === 0 && <div className="text-sm text-slate-400">Noch keine Vorlagen.</div>}
            {(c.confirmation_templates || []).map((t, i) => (
              <div key={t.id || i} className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`tpl-${t.id}`}>
                <div><div className="font-medium text-slate-900 text-sm">{t.name}</div><div className="text-sm text-slate-500 line-clamp-2">{t.text}</div></div>
                <button onClick={() => delTpl(t.id)} data-testid={`tpl-delete-${t.id}`} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="grid gap-2 pt-2 border-t border-slate-100">
            <Input placeholder="Vorlagen-Name" data-testid="tpl-name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} />
            <Textarea placeholder="Standardtext…" data-testid="tpl-text" value={tpl.text} onChange={(e) => setTpl({ ...tpl, text: e.target.value })} />
            <Button onClick={addTpl} variant="outline" data-testid="tpl-add" className="justify-self-start"><Plus className="h-4 w-4 mr-2" /> Vorlage hinzufügen</Button>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 rounded-xl space-y-4">
          <h3 className="font-semibold text-slate-900">Kunden-Merkliste</h3>
          <div className="space-y-2">
            {(c.clients || []).length === 0 && <div className="text-sm text-slate-400">Noch keine Kunden.</div>}
            {(c.clients || []).map((x, i) => (
              <div key={x.id || i} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`client-${x.id}`}>
                <div><div className="font-medium text-slate-900 text-sm">{x.name}</div><div className="text-sm text-slate-500">{[x.phone, x.email, x.address].filter(Boolean).join(" · ")}</div></div>
                <button onClick={() => delClient(x.id)} data-testid={`client-delete-${x.id}`} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <Input placeholder="Name" data-testid="client-name" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
            <Input placeholder="Telefon" data-testid="client-phone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
            <Input placeholder="E-Mail" data-testid="client-email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} />
            <Input placeholder="Adresse" data-testid="client-address" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} />
            <Button onClick={addClient} variant="outline" data-testid="client-add" className="justify-self-start sm:col-span-2"><Plus className="h-4 w-4 mr-2" /> Kunde hinzufügen</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
